'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { UserCog, X, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';

/**
 * ImpersonationBanner Component - CR-006-U154
 *
 * Displays a prominent banner at the top of the application when a super admin
 * is impersonating another user. Provides clear visual indication and quick exit.
 *
 * Features:
 * - Shows impersonated user's name and email
 * - Displays session duration (auto-updates)
 * - Prominent warning styling with gradient
 * - Exit button to stop impersonation
 * - Auto-refreshes page after exiting impersonation
 * - Audit trail logged via backend API
 */
export function ImpersonationBanner() {
  const router = useRouter();
  const [stopping, setStopping] = useState(false);
  const [sessionDuration, setSessionDuration] = useState('');

  // Check if impersonating
  const isImpersonating = apiClient.isImpersonating();

  useEffect(() => {
    if (!isImpersonating) return;

    // Update session duration every minute
    const updateDuration = () => {
      const startedAt = localStorage.getItem('impersonation_started_at');
      if (startedAt) {
        const start = new Date(startedAt);
        const now = new Date();
        const diffMs = now.getTime() - start.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const mins = diffMins % 60;

        if (diffHours > 0) {
          setSessionDuration(`${diffHours}h ${mins}m`);
        } else if (diffMins > 0) {
          setSessionDuration(`${mins}m`);
        } else {
          setSessionDuration('Just now');
        }
      }
    };

    updateDuration();
    const interval = setInterval(updateDuration, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [isImpersonating]);

  if (!isImpersonating) {
    return null;
  }

  const impersonatedUser = apiClient.getImpersonatedUserInfo();

  const handleStopImpersonation = async () => {
    if (!confirm('Are you sure you want to exit impersonation and return to your admin account?')) {
      return;
    }

    try {
      setStopping(true);
      await apiClient.stopImpersonation();

      // Redirect back to super admin users page
      router.push('/super-admin/users');

      // Force page reload to clear all state
      window.location.href = '/super-admin/users';
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      alert('Failed to stop impersonation. Please try again.');
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <UserCog className="h-5 w-5 flex-shrink-0 animate-pulse" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">
              Impersonation Mode Active
            </p>
            <div className="flex items-center gap-2 flex-wrap text-xs opacity-90 mt-0.5">
              <span>
                Viewing as{' '}
                <span className="font-medium">
                  {impersonatedUser.userName || impersonatedUser.userEmail}
                </span>
                {impersonatedUser.userEmail && impersonatedUser.userName && (
                  <span className="opacity-75"> ({impersonatedUser.userEmail})</span>
                )}
              </span>
              {sessionDuration && (
                <span className="flex items-center gap-1 border-l border-white/30 pl-2">
                  <Clock className="h-3 w-3" />
                  {sessionDuration}
                </span>
              )}
            </div>
          </div>
        </div>

        <Button
          onClick={handleStopImpersonation}
          disabled={stopping}
          size="sm"
          variant="secondary"
          className="flex-shrink-0 bg-white text-orange-600 hover:bg-orange-50 font-medium"
        >
          {stopping ? (
            <>
              <div className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-orange-600 mr-2"></div>
              Exiting...
            </>
          ) : (
            <>
              <X className="h-4 w-4 mr-1" />
              Exit Impersonation
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
